<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="filter-row-single">
        <!-- Stock Allocation Date Picker -->
        <div class="filter-item">
          <label class="filter-label" for="startDatePicker">Stock Allocation Date</label>
          <input
            id="startDatePicker"
            type="date"
            v-model="startDate"
            class="form-control filter-control"
          />
        </div>

        <!-- Sales Rep / Vehicle Dropdown -->
        <div class="filter-item">
          <label class="filter-label" for="salesRepSelect">Sales Rep (Vehicle No)</label>
          <select id="salesRepSelect" v-model="selectedSalesRep" class="form-select filter-control">
            <option value="">All Sales Reps / Vehicles</option>
            <option v-for="rep in salesRepsList" :key="rep.profile_id" :value="String(rep.profile_id)">
              {{ rep.name }}
            </option>
          </select>
        </div>

        <!-- Search Button -->
        <div class="filter-item button-item">
          <button @click="performSearch" class="btn button-default btn-search-single" :disabled="loading">
            <span>Search</span>
          </button>
        </div>

        <!-- Create Stock Allocation Button -->
        <div class="filter-item button-item">
          <button type="button" class="btn button-default new-stock-allocation-btn" @click="newStockAllocation">
            Create Stock Allocation
          </button>
        </div>
      </div>

      <!-- Loading Spinner (Table hidden when loading) -->
      <div v-if="loading" class="loading-spinner">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <!-- Table Container (Displayed only when loading is complete and data exists) -->
      <div v-else-if="filteredBootstrapList && filteredBootstrapList.length > 0" class="table-container table-responsive">
        <table class="table table-compact">
          <thead>
          <tr class="border-bottom-primary">
            <th scope="col">Stock Allocation No</th>
            <th scope="col">Type</th>
            <th scope="col">DSR (Vehicle No)</th>
            <th scope="col">Created At</th>
            <th scope="col">Dispatched/DSR Accepted At</th>
            <th scope="col">Status</th>
            <th scope="col">Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="(item, index) in filteredBootstrapList"
              :key="index"
              :class="item.borderColor"
          >
            <td>{{ item.stock_allocations_no }}</td>
            <td>
              <span :class="getTypeLabelClass(item.is_temp)">
                {{ getTypeLabel(item.is_temp) }}
              </span>
            </td>
            <td>{{ (item.dealer_name ? item.dealer_name + ' ' : '') + (item.sales_rep_name || '') }}</td>
            <td>{{ formatDate(item.created_at) }}</td>
            <td>{{ getDispatchOrConfirmedDate(item) }}</td>
            <td>
              <span :class="getStatusLabelClass(item)">
                {{ getDispatchStatus(item) }}
              </span>
            </td>
            <td>
              <i v-if="getDispatchStatus(item) === 'Pending'" @click="editDetails(item.id)" class="fa fa-edit" aria-hidden="true"></i>
              <i @click="viewDetails(item.id)" class="fa fa-eye action-icon" aria-hidden="true"></i>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Modern Empty State (Displayed when loading is complete and no data found) -->
      <div v-else class="no-data-container">
        <div class="no-data-card">
          <div class="no-data-icon">
            <i class="fa fa-folder-open-o"></i>
          </div>
          <h5 class="no-data-title">No Stock Allocations Found</h5>
          <p class="no-data-text">There are no stock allocation records available for the selected date.</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Stock Allocation View Modal Component -->
  <StockAllocationViewModal
    :showModal="showPopup"
    :allocationId="selectedAllocationId"
    @close="closePopup"
  />
</template>

<script setup>
import { useRouter } from "vue-router";
import { ref, computed, onMounted } from 'vue';
import { fetchAllocationList, fetchSalesReps } from '@/services/dataService';
import StockAllocationViewModal from '@/pages/Modal/stockAllocationViewModal.vue';

const router = useRouter();
const bootstraplist = ref([]);
const salesRepsList = ref([]);
const selectedSalesRep = ref('');

const profileId = ref(localStorage.getItem('profile_id'));
const userRole = localStorage.getItem('user_label');
const token = ref(localStorage.getItem('token_set') || '');
const allocationDetails = ref([]);
const showModal = ref(false);
const startDate = ref('');
const initialLoad = ref(true);
const loading = ref(false);
const selectedAllocationId = ref(null);
const showPopup = ref(false);

const filteredBootstrapList = computed(() => {
  if (!selectedSalesRep.value) {
    return bootstraplist.value;
  }
  const selectedRep = salesRepsList.value.find(
    r => String(r.profile_id) === String(selectedSalesRep.value)
  );

  return bootstraplist.value.filter(item => {
    const repIdMatches = String(item.sales_rep_id || item.sales_rep_profile_id || '') === String(selectedSalesRep.value);
    let nameMatches = false;
    if (selectedRep && selectedRep.name && item.sales_rep_name) {
      const targetName = selectedRep.name.toLowerCase().trim();
      const repName = item.sales_rep_name.toLowerCase().trim();
      nameMatches = repName.includes(targetName) || targetName.includes(repName);
    }
    return repIdMatches || nameMatches;
  });
});

const fetchSalesRepsData = async () => {
  try {
    const response = await fetchSalesReps(profileId.value, profileId.value, token.value);
    const list = response?.data?.data || [];
    salesRepsList.value = list.map(r => ({
      profile_id: String(r.profile_id),
      name: r.name
    }));
  } catch (error) {
    console.error("Error fetching sales reps:", error);
  }
};

const newStockAllocation = () => {
  router.push({ path: '/new-stock-allocation' });
};

const getTypeLabel = (isTemp) => {
  return (isTemp === '1' || isTemp === 1) ? 'Temporary' : 'Original';
};

const getTypeLabelClass = (isTemp) => {
  return (isTemp === '1' || isTemp === 1) ? 'badge-type type-temp' : 'badge-type type-original';
};

const getDispatchStatus = (item) => {
  const isTemp = item.is_temp === '1' || item.is_temp === 1;
  const isDispatched = item.is_dispatched === '1' || item.is_dispatched === 1;
  const isConfirmed = item.is_sales_rep_confirmed === '1' || item.is_sales_rep_confirmed === 1;

  if (isConfirmed && isDispatched) {
    return 'DSR Accepted';
  } else if (isDispatched) {
    return 'Dispatched';
  } else if (isTemp) {
    return 'Pending';
  }
  return 'Pending';
};

const getStatusLabelClass = (item) => {
  const isTemp = item.is_temp === '1' || item.is_temp === 1;
  const isDispatched = item.is_dispatched === '1' || item.is_dispatched === 1;
  const isConfirmed = item.is_sales_rep_confirmed === '1' || item.is_sales_rep_confirmed === 1;

  if (isConfirmed && isDispatched) {
    return 'badge-status badge-accepted';
  } else if (isDispatched) {
    return 'badge-status badge-dispatched';
  } else if (isTemp) {
    return 'badge-status badge-pending';
  }
  return 'badge-status badge-pending';
};

const formatDate = (dateStr) => {
  if (!dateStr || dateStr.startsWith('1900-01-01') || dateStr.startsWith('0000-00-00')) {
    return '-';
  }
  const formattedStr = dateStr.includes(' ') && !dateStr.includes('T') ? dateStr.replace(' ', 'T') : dateStr;
  const date = new Date(formattedStr);
  if (isNaN(date.getTime())) {
    return dateStr;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const formattedHours = String(hours).padStart(2, '0');

  return `${year}-${month}-${day} ${formattedHours}:${minutes} ${ampm}`;
};

const getDispatchOrConfirmedDate = (item) => {
  if (item.is_temp === '1' || item.is_temp === 1) {
    return formatDate(item.sales_rep_confirmed_at);
  }
  return formatDate(item.dispatch_created_at);
};

const loadStockAllocations = async (date) => {
  try {
    loading.value = true;
    bootstraplist.value = [];
    const payload = {
      created_by: profileId.value,
      date: date || new Date().toISOString().split('T')[0]
    };
    if (selectedSalesRep.value) {
      payload.sales_rep_id = selectedSalesRep.value;
      payload.rep_id = selectedSalesRep.value;
    }
    const response = await fetchAllocationList(profileId.value, token.value, payload);
    if (response.data.status === 'failed') {
      console.log(response.data.message);
    } else {
      bootstraplist.value = response.data.data;
    }
  } catch (error) {
    console.error("Error fetching stock allocations:", error);
  } finally {
    loading.value = false;
  }
};

const performSearch = () => {
  initialLoad.value = false;
  loadStockAllocations(startDate.value);
};

const viewDetails = (id) => {
  selectedAllocationId.value = id;
  showPopup.value = true;
};

const editDetails = (id) => {
  router.push({ path: `/edit-stock-allocation/${id}` });
};

const closePopup = () => {
  showPopup.value = false;
};

const getCurrentDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateTime = (dateTimeString) => {
  const date = new Date(dateTimeString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

onMounted(async () => {
  try {
    startDate.value = getCurrentDate();
    await fetchSalesRepsData();
    loadStockAllocations(startDate.value);
  } catch (error) {
    console.error("Initialization failed:", error);
  }
});
</script>

<style scoped lang="scss">
.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

/* Single Line Filter Row Layout */
.filter-row-single {
  display: flex;
  align-items: flex-end;
  flex-wrap: nowrap;
  gap: 16px;
  margin: 20px 20px 10px 20px;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.filter-item.button-item {
  flex: 0 0 auto;
}

.filter-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0;
  white-space: nowrap;
}

.filter-control {
  height: 42px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  font-size: 0.9rem;
  color: #1e293b;
  padding: 0 12px;
  width: 100%;
  background-color: #ffffff;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
    outline: none;
  }
}

.btn-search-single {
  height: 42px;
  padding: 0 20px;
  background-color: #007bff;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 6px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-sizing: border-box;

  &:hover {
    background-color: #0056b3;
    box-shadow: 0 4px 6px -1px rgba(0, 123, 255, 0.2);
  }
}

.new-stock-allocation-btn {
  height: 42px;
  padding: 0 20px;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
}

.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}

.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.fa-eye:active {
  transform: scale(1.1);
}

.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

.fa-edit:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.fa-edit:active {
  transform: scale(1.1);
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #000;
  transition: color 0.3s;
}

.popup-content h3 {
  margin-top: 0;
}

.popup-content ul {
  padding-left: 20px;
}

.popup-content ul li {
  margin-bottom: 10px;
}

.action-icon {
  margin-right: 10px;
}

.new-stock-allocation-btn {
  margin-left: auto;
  padding: 9px 20px;
  height: 42px;
}

/* Table Container with Margin & Fixed Max Height */
.table-container {
  margin: 15px 20px 20px 20px;
  max-height: 520px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* Compact Table Styling */
.table-compact {
  width: 100%;
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;

  th {
    padding: 10px 14px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #4a5568;
    background-color: #f7fafc;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;
  }

  td {
    padding: 9px 14px;
    font-size: 13px;
    color: #2d3748;
    vertical-align: middle;
    white-space: nowrap;
    border-bottom: 1px solid #edf2f7;
  }

  tbody tr:hover {
    background-color: #f8fafc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

/* Modern Empty State Styling */
.no-data-container {
  margin: 15px 20px 20px 20px;
}

.no-data-card {
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e0;
  border-radius: 8px;
  padding: 50px 25px;
  width: 100%;
}

.no-data-icon {
  font-size: 42px;
  color: #a0aec0;
  margin-bottom: 12px;
}

.no-data-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 6px;
}

.no-data-text {
  font-size: 13px;
  color: #718096;
  margin: 0;
}

/* Badge Styling - Small & Uniform Dimensions */
.badge-type {
  display: inline-block;
  min-width: 85px;
  width: 85px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  border-radius: 4px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.type-original {
  background-color: #e3f2fd;
  color: #0d47a1;
  border: 1px solid #bbdefb;
}

.type-temp {
  background-color: #fff3e0;
  color: #e65100;
  border: 1px solid #ffe0b2;
}

.badge-status {
  display: inline-block;
  min-width: 105px;
  width: 105px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  border-radius: 4px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #ffffff;
}

.badge-dispatched,
.badge-accepted {
  background-color: #069e27;
  border: 1px solid #058521;
}

.badge-pending {
  background-color: #d32f2f;
  border: 1px solid #b71c1c;
}

</style>